/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/quotes */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { LibraryService } from '@core/services/library/library.service';
import { AlertController } from '@ionic/angular';

@Component({
    selector: 'app-audio-transcriber',
    templateUrl: './audio-transcriber.component.html',
    styleUrls: ['./audio-transcriber.component.scss'],
    standalone: false
})
export class AudioTranscriberComponent implements OnInit {
    @Input() disabled = false;
    @Input() animationPosition: 'top' | 'left' | 'right' | 'bottom' = 'right';
    @Output() partialTranscription = new EventEmitter<any>();
    @Output() finalTranscription = new EventEmitter<any>();

    MAX_RETRIES = 3;
    TIMEOUT_MS = 3000;

    iconName = 'mic';
    isRecording = false;
    showVoiceAnimation = false;
    permissionStatus = '';
    text = '';
    stopRecordTimer: any;
    retryTimer: any = undefined;
    retries = 0;
    hasStartedRecording = false;

    constructor(
        private alertController: AlertController,
        private libraryService: LibraryService
    ) { }

    ngOnInit(): void {
    }

    /**
     * Activa o desactiva la grabación
     */
    async toggleRecording() {
        if (!this.disabled) {
            SpeechRecognition.checkPermissions().then(data => {
                if (data.speechRecognition !== 'granted') {
                    SpeechRecognition.requestPermissions();
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                this.isRecording ? this.stopRecording() : this.startRecording();
            });
        }
    }

    /**
     * Comienza la grabación
     */
    startRecording() {
        try {
            this.startSpeechRecognition();
        } catch (error) {
            console.error('Error al iniciar la grabación:', error);
            this.setRecordingAnimation(false);
        }
    }

    /**
     * Detiene la grabación
     */
    stopRecording() {
        this.setRecordingAnimation(false);
        this.finalTranscription.emit({ detail: this.capitalizeFirstLetter(this.text) });
        this.text = '';
        SpeechRecognition.removeAllListeners();
        SpeechRecognition.stop();
        this.hasStartedRecording = false;
        this.retries = 0;
    }

    /**
     * Vuelve a intentar grabar
     */
    stopAndRetry() {
        this.retries++;
        SpeechRecognition.removeAllListeners();
        this.startRecording();
    }

    /**
     * Inicia el componente de reconocimiento de voz
     */
    startSpeechRecognition() {
        if (this.retries > this.MAX_RETRIES) {
            this.createRetryAlert();
            return;
        }

        this.setRecordingAnimation(true);

        SpeechRecognition.start({
            language: navigator.language,
            maxResults: 1,
            partialResults: true,
            popup: false
        });

        SpeechRecognition.addListener('partialResults', (data: any) => {
            this.hasStartedRecording = true;
            if (this.retryTimer !== undefined) {
                clearTimeout(this.retryTimer);
                this.retryTimer = undefined;
            }
            this.text = data.matches[0];
            this.text = this.capitalizeFirstLetter(this.text).trim();

            this.partialTranscription.emit({ detail: this.text });
        });

        SpeechRecognition.addListener('listeningState', ({ status }) => {
            if (status === 'stopped' && this.text !== '') {
                this.setRecordingAnimation(false);
                this.stopRecordTimer = setTimeout(() => {
                    this.finalTranscription.emit({ detail: this.capitalizeFirstLetter(this.text) });
                    this.stopRecording();
                    this.text = '';
                }, 2000);
            } else {
                if (this.stopRecordTimer) {
                    clearTimeout(this.stopRecordTimer);
                    this.stopRecordTimer = null;
                }
            }
        });
        this.initRetryTime();
    }

    /**
     * Muestra u oculta la animación de grabación
     * @param status Estado de la animación
     */
    setRecordingAnimation(status: boolean) {
        this.isRecording = status;
        this.showVoiceAnimation = status;
    }

    /**
     * Crea el alert de error de intentar de nuevo
     */
    async createRetryAlert() {
        const alert = await this.alertController.create({
            message: this.libraryService.getLabel('lbl_transcription_failed'),
            cssClass: 'user-pin-alert',
            buttons: [
                {
                    text: this.libraryService.getLabel('lbl_go_back'),
                    role: 'cancel'
                }
            ]
        });

        this.setRecordingAnimation(false);
        this.hasStartedRecording = false;
        this.retries = 0;

        await alert.present();
    }

    /**
     * Inicia el temporizador para comprobar si el micrófono está detectando sonido o no
     */
    initRetryTime() {
        this.retryTimer = setTimeout(() => {
            if (!this.hasStartedRecording) {
                this.stopAndRetry();
            }
        }, this.TIMEOUT_MS);
    }

    /**
     * Pone la primera letra de un string en mayúscula
     * @param str String
     * @returns String capitalizado
     */
    capitalizeFirstLetter(str): string {
        if (!str || str.length === 0) {
            return '';
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
